import {parseQueryString} from '../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {AppRoutesPrefixesEnum} from '../../../../components/appRoutesPrefixes';
import {AppState} from '../../../appState';
import {PanelRoute} from '../../panelRoutes';
import {
  ContactsManagerParameters,
  ContactsManagerParametersTypesEnum,
  RContactsManagerSearchTab,
} from './parameters/contactsManagerParameters';

/*
 * Types.
 */

export type ContactsManagerPanelRoute = PanelRoute<AppState, ContactsManagerParameters | undefined>;

/*
 * Routes.
 */

// Routes are matched from top to bottom. So the more specific routes need to be at the top.
export function buildContactsManagerPanelRoutes(): ReadonlyArray<ContactsManagerPanelRoute> {
  return [
    // Full Page Syncs Errors.
    {
      path: '/syncs/:source/errors',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.SYNCS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/syncs/${params.source}/errors`,
        },
        source: params.source,
        shouldShowErrors: true,
      }),
    },
    // Full Page Syncs.
    {
      path: '/syncs/:source',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.SYNCS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/syncs/${params.source}`,
        },
        source: params.source,
      }),
    },
    // Account Syncs Errors.
    {
      path: '/accounts/syncs/:source/errors',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNT_SYNCS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/syncs/${params.source}/errors`,
        },
        source: params.source,
        shouldShowErrors: true,
      }),
    },
    // Account Syncs.
    {
      path: '/accounts/syncs/:source',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNT_SYNCS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/syncs/${params.source}`,
        },
        source: params.source,
      }),
    },
    // Account contacts with contact details.
    {
      path: '/accounts/:accountId/contacts/:cardId',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${params.accountId}/contacts/${params.cardId}`,
        },
        accountId: Number(params.accountId),
        cardId: Number(params.cardId),
      }),
    },
    // Account contacts.
    {
      path: '/accounts/:accountId/contacts',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${params.accountId}/contacts`,
        },
        accountId: Number(params.accountId),
      }),
    },
    // Import accounts with csv.
    {
      path: '/accounts/csv-import',
      getParameters: () => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNTS_CSV_IMPORT,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/csv-import`,
        },
      }),
    },
    // Accounts with account details.
    {
      path: '/accounts/:accountId',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${params.accountId}`,
        },
        accountId: Number(params.accountId),
      }),
    },
    // Accounts.
    {
      path: '/accounts',
      getParameters: () => ({
        type: ContactsManagerParametersTypesEnum.ACCOUNTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`,
        },
      }),
    },

    // Lists.
    {
      path: '/contacts/lists/:cardGroupId/:cardId',
      getParameters: (_state, {params}) => {
        const {cardGroupId, cardId} = params;
        if (cardGroupId && cardGroupId === 'all') {
          return {
            type: ContactsManagerParametersTypesEnum.LISTS,
            location: {
              pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/all`,
            },
          };
        }
        return {
          type: ContactsManagerParametersTypesEnum.LIST_CONTACTS,
          location: {
            pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/${cardGroupId}/${cardId}`,
          },
          cardGroupId: Number(cardGroupId),
          cardId: Number(cardId),
        };
      },
    },
    {
      path: '/contacts/lists/:cardGroupId',
      getParameters: (_state, {params}) => {
        const {cardGroupId} = params;
        if (cardGroupId && cardGroupId === 'all') {
          return {
            type: ContactsManagerParametersTypesEnum.LISTS,
            location: {
              pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/all`,
            },
          };
        }
        return {
          type: ContactsManagerParametersTypesEnum.LIST_CONTACTS,
          location: {
            pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/${cardGroupId}`,
          },
          cardGroupId: Number(cardGroupId),
        };
      },
    },
    // Lists (Legacy).
    {
      path: '/contacts/groups/:cardGroupId',
      getParameters: (_state, {params}) => {
        const {cardGroupId} = params;
        if (cardGroupId && cardGroupId === 'all') {
          return {
            type: ContactsManagerParametersTypesEnum.LISTS,
            location: {
              pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/groups/all`,
            },
          };
        }
        return {
          type: ContactsManagerParametersTypesEnum.LIST_CONTACTS,
          location: {
            pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/groups/${cardGroupId}`,
          },
          cardGroupId: Number(cardGroupId),
        };
      },
    },

    // Company contacts with contact details.
    {
      path: '/contacts/global/:cardId',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.COMPANY_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/global/${params.cardId}`,
        },
        cardId: Number(params.cardId),
      }),
    },
    // Company contacts.
    {
      path: '/contacts/global',
      getParameters: () => ({
        type: ContactsManagerParametersTypesEnum.COMPANY_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/global`,
        },
      }),
    },
    // Other contacts with contact details.
    {
      path: '/contacts/other/:cardId',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.OTHER_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/other/${params.cardId}`,
        },
        cardId: Number(params.cardId),
      }),
    },
    // Other contacts.
    {
      path: '/contacts/other',
      getParameters: () => ({
        type: ContactsManagerParametersTypesEnum.OTHER_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/other`,
        },
      }),
    },
    // Private contacts with contact details.
    {
      path: '/contacts/:namespace/:cardId',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/${params.namespace}/${params.cardId}`,
        },
        namespace: params.namespace,
        cardId: Number(params.cardId),
      }),
    },
    // Private contacts.
    {
      path: '/contacts/:namespace',
      getParameters: (_state, {params}) => ({
        type: ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/${params.namespace}`,
        },
        namespace: params.namespace,
      }),
    },

    // Search.
    {
      path: '/search/:query',
      getParameters: (_state, {params, search}) => ({
        type: ContactsManagerParametersTypesEnum.SEARCH,
        location: {
          pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/search/${params.query}`,
        },
        query: decodeURIComponent(params.query),
        tab: maybeParseTabFromSearch(search),
      }),
    },
  ];
}

/*
 * Helpers.
 */

function maybeParseTabFromSearch(search: string) {
  const {tab} = parseQueryString(search);
  if (RContactsManagerSearchTab.is(tab)) {
    return tab;
  }
  return undefined;
}
